// @ts-check

import QrScanner from 'qr-scanner';
import { MiiDecoder, MiiExtraInfo } from '../MiiDecoderCli.js';
import { WrappedStoreData, NativeAesCcmCipher } from '../../WrappedStoreData/WrappedStoreDataTest.js';

// // -------------------------------------------------------
// // Codec utilities
// // -------------------------------------------------------

const base64ToBytes = (/** @type {string} */ base64) =>
  Uint8Array.from(atob(base64), c => c.charCodeAt(0));

const hexToBytes = (/** @type {string} */ hex) => Uint8Array.from(
  { length: hex.length >>> 1 }, (_, i) =>
    Number.parseInt(hex.slice(i << 1, (i << 1) + 2), 16));

const bytesToHex = (/** @type {ArrayLike<number>} */ bytes) => Array.prototype.map.call(bytes,
  (/** @type {{ toString: function(number): string; }} */ x) =>
    x.toString(16).padStart(2, '0')).join(''); // padStart: ES2017

/** Decodes Base64 or Base64URL to Uint8Array, adding padding if missing. */
const base64ExToBytes = (/** @type {string} */ base64) => {
  // Replace URL-safe characters with regular Base64 equivalents.
  base64 = base64.replace(/-/g, '+').replace(/_/g, '/');
  // Add padding to the Base64 string if it is missing.
  while (base64.length % 4 !== 0) {
    base64 += '=';
  }
  return base64ToBytes(base64);
};

/** Parses either hex or Base64 -> U8, stripping spaces from the input. */
const parseHexOrBase64ExToBytes = (/** @type {string} */ text) => {
  text = text.replace(/\s+/g, ''); // Strip spaces.
  // Check if it is hex, otherwise assume it is Base64.
  return /^[0-9a-fA-F]+$/.test(text)
    ? hexToBytes(text)
    : base64ExToBytes(text);
};

// // -------------------------------------------------------
// // Enums
// // -------------------------------------------------------

/**
 * Error reasons for Mii import failures.
 * @enum {number}
 */
export const MiiErrorReason = {
  /** Data size does not match any known Mii format. */
  UnsupportedSize: 0,
  /** Converted studio data failed MiiDecoder.isValid(). */
  ValidationFailed: 1,
  /** QR code was found but contained no binary payload. */
  QrNoBinary: 2,
  /** QR code scanner failed while scanning. */
  QrScanError: 3,
  /** No QR code detected in uploaded image. */
  QrNotFound: 4,
  /** File could not be read from disk. */
  FileReadError: 5
};

/**
 * Default English friendly messages for each error reason.
 * Consumers can use this table or provide their own localized messages.
 * @type {Map<number, string>}
 */
const ErrorMessageTable = new Map([
  [MiiErrorReason.UnsupportedSize, 'Data size does not match any known Mii format.'],
  [MiiErrorReason.ValidationFailed, 'Decoded data failed validation check.'],
  [MiiErrorReason.QrNoBinary, 'QR code found but contains no binary data.'],
  [MiiErrorReason.QrScanError, 'Unexpected QR code scanner error. (Camera not present? Permissions not granted?)'],
  [MiiErrorReason.QrNotFound, 'No QR code detected in uploaded image.'],
  [MiiErrorReason.FileReadError, 'Failed to read the file.']
]);

/**
 * Source type identifiers.
 * @enum {number}
 */
export const ImportSourceType = {
  Text: 0,
  File: 1,
  Qr: 2,
  Custom: 3
};

// // -------------------------------------------------------
// // Event classes
// // -------------------------------------------------------

/** Fired when Mii data has been successfully decoded. */
export class ImportParsedEvent {
  /**
   * @param {symbol} sourceId - Which source produced this data.
   * @param {Uint8Array} rawData - Original input bytes before conversion.
   * @param {Uint8Array} studioData - 46-byte converted Studio data.
   * @param {string} studioHexUrl - Obfuscated hex string for the Studio image URL.
   * @param {MiiExtraInfo} extraInfo - Extra metadata extracted from the source format.
   */
  constructor(sourceId, rawData, studioData, studioHexUrl, extraInfo) {
    /** @readonly */ this.sourceId = sourceId;
    /** @readonly */ this.rawData = rawData;
    /** @readonly */ this.studioData = studioData;
    /** @readonly */ this.studioHexUrl = studioHexUrl;
    /** @readonly */ this.extraInfo = extraInfo;
  }

  /** @returns {string} Full URL to the rendered Mii image. */
  getImageUrl() {
    return MiiDecoder.URL_BASE_IMAGE_PNG + this.studioHexUrl;
  }
}

/** Fired when Mii data import encounters an error. */
export class ImportErrorEvent {
  /**
   * @param {symbol} sourceId - Which source produced the error.
   * @param {MiiErrorReason} reason - Error reason enum value.
   * @param {Error} [cause] - Optional underlying error for debugging.
   */
  constructor(sourceId, reason, cause) {
    /** @readonly */ this.sourceId = sourceId;
    /** @readonly */ this.reason = reason;
    /** @readonly */ this.cause = cause || null;
  }

  /**
   * Get a default English friendly message for this error.
   * Consumers who need localization should use the reason enum
   * to look up their own strings instead.
   * @returns {string}
   */
  getFriendlyMessage() {
    return ErrorMessageTable.get(this.reason) || 'Unknown error.';
  }
}

/** Fired when the active source changes. */
export class ImportSourceChangedEvent {
  /**
   * @param {symbol} activeSourceId - Newly active source.
   * @param {symbol | null} previousSourceId - Previously active source, or null.
   */
  constructor(activeSourceId, previousSourceId) {
    /** @readonly */ this.activeSourceId = activeSourceId;
    /** @readonly */ this.previousSourceId = previousSourceId;
  }
}

/**
 * Fired when QR scanner cameras have been enumerated.
 * Emitted after the scanner starts and listCameras resolves.
 */
export class ImportCamerasLoadedEvent {
  /** @param {Array<{id: string, label: string}>} cameras */
  constructor(cameras) {
    /** @readonly */ this.cameras = cameras;
  }
}

// // -------------------------------------------------------
// // Internal helpers
// // -------------------------------------------------------

/**
 * Build the obfuscated hex string used in Studio image URLs.
 * @param {Uint8Array} studioData - 46-byte raw Studio data.
 * @returns {string} Hex-encoded 47-byte obfuscated data.
 */
function buildStudioHexUrl(studioData) {
  const obfuscated = new Uint8Array(MiiDecoder.SIZE_STUDIO_URL_DATA);
  MiiDecoder.obfuscateStudioUrl(obfuscated, studioData, 0);
  return bytesToHex(obfuscated);
}

/**
 * Attempt to decrypt WrappedStoreData (encrypted Mii QR code).
 * Returns the decrypted data, or the original data if it is not wrapped.
 * @param {Uint8Array} data - Input data that might be encrypted.
 * @returns {Uint8Array} Decrypted or original data.
 */
function decryptIfWrappedStoreData(data) {
  if (data.length < WrappedStoreData.LENGTH) {
    return data; // No Mii data format is larger than 96, so larger data is likely a QR code.
  }

  // Initialize AES-CCM cipher with the 0x31 production key.
  const cipher = new NativeAesCcmCipher();
  cipher.initialize(/* @__PURE__ */ new Uint8Array([
    0x59, 0xFC, 0x81, 0x7E, 0x64, 0x46, 0xEA, 0x61,
    0x90, 0x34, 0x7B, 0x20, 0xE9, 0xBD, 0xCE, 0x52
  ]), 0);
  const unwrapped = new Uint8Array(WrappedStoreData.STORE_DATA_LENGTH);
  WrappedStoreData.decrypt(unwrapped, 0, data, 0, cipher);
  return unwrapped;
}

// // -------------------------------------------------------
// // Internal record classes
// // -------------------------------------------------------

/** Tracks a single DOM event listener for cleanup. */
class ListenerRecord {
  /**
   * @param {EventTarget} target
   * @param {string} event
   * @param {EventListener} handler
   * @param {AddEventListenerOptions} [options]
   */
  constructor(target, event, handler, options) {
    this.target = target;
    this.event = event;
    this.handler = handler;
    this.options = options;
  }

  /** Remove this listener from its target. */
  remove() {
    this.target.removeEventListener(this.event, this.handler, this.options);
  }
}

/**
 * Internal record for a registered import source.
 * Each source type stores a cleanup callback set during registration.
 */
class SourceRecord {
  /**
   * @param {symbol} id
   * @param {ImportSourceType} type
   */
  constructor(id, type) {
    this.id = id;
    this.type = type;
    /** @type {Uint8Array | null} Last raw input bytes. */
    this.lastRawData = null;
    /** @type {Uint8Array | null} Last decoded Studio data. */
    this.lastStudioData = null;
    /** @type {MiiExtraInfo | null} Last decoded extra info. */
    this.lastExtraInfo = null;
    /** @type {Array<ListenerRecord>} DOM listeners to clean up. */
    this.listeners = [];
    /** @type {*} Type-specific state (debounce timer, scanner, etc.). */
    this.extra = null;
    /**
     * Optional type-specific cleanup. Set by the add* method.
     * @type {(function(): void) | null}
     * @package
     */
    this._disposeExtra = null;
  }

  /** Dispose of this source: remove listeners and run type-specific cleanup. */
  dispose() {
    for (const record of this.listeners) {
      record.remove();
    }
    this.listeners.length = 0;
    if (this._disposeExtra) {
      this._disposeExtra();
      this._disposeExtra = null;
    }
  }
}

/**
 * Register a DOM event listener and track it on a source record.
 * @param {SourceRecord} source - Source to track on.
 * @param {EventTarget} target - Element or object to listen on.
 * @param {string} event - Event name.
 * @param {EventListener} handler - Callback.
 * @param {AddEventListenerOptions} [options] - Listener options.
 */
function trackListener(source, target, event, handler, options) {
  target.addEventListener(event, handler, options);
  source.listeners.push(new ListenerRecord(target, event, handler, options));
}

// // -------------------------------------------------------
// // Controller
// // -------------------------------------------------------

/**
 * @callback ImportParsedHandler
 * @param {ImportParsedEvent} event
 * @returns {void}
 */

/**
 * @callback ImportErrorHandler
 * @param {ImportErrorEvent} event
 * @returns {void}
 */

/**
 * @callback ImportSourceChangedHandler
 * @param {ImportSourceChangedEvent} event
 * @returns {void}
 */

/**
 * @callback ImportScanningHandler
 * @returns {void}
 */

/**
 * @callback ImportScannerStoppedHandler
 * @returns {void}
 */

/**
 * @callback ImportCamerasLoadedHandler
 * @param {ImportCamerasLoadedEvent} event
 * @returns {void}
 */

/**
 * Orchestrates Mii data import from multiple user-provided input sources.
 * Does not create or own any DOM elements — the consumer provides them
 * and controls all visual presentation including text and localization.
 */
export class MiiImportController {
  /**
   * @type {Map<symbol, SourceRecord>}
   * @private
   */
  _sources = new Map();

  /**
   * @type {symbol | null}
   * @private
   */
  _activeSourceId = null;

  /**
   * @type {Map<string, Set<Function>>}
   * @private
   */
  _eventHandlers = new Map();

  /**
   * @type {boolean}
   * @private
   */
  _disposed = false;

  // // -------------------------------------------------------
  // // Event emitter
  // // -------------------------------------------------------

  /**
   * a
   * @typedef {'parsed' | 'error' | 'sourceChanged' | 'scanning' |
   * 'scannerStopped' | 'camerasLoaded'} ImportEventType
   */

  /**
   * s
   * @typedef {ImportParsedHandler | ImportErrorHandler | ImportSourceChangedHandler |
   * ImportScanningHandler | ImportScannerStoppedHandler} ImportEventHandler
   */

  /**
   * Register an event handler.
   * Supported events:
   * - `'parsed'`: {@link ImportParsedHandler}
   * - `'error'`: {@link ImportErrorHandler}
   * - `'sourceChanged'`: {@link ImportSourceChangedHandler}
   * - `'scanning'`: {@link ImportScanningHandler}
   * - `'scannerStopped'`: {@link ImportScannerStoppedHandler}
   * - `'camerasLoaded'`: {@link ImportCamerasLoadedHandler}
   * @param {ImportEventType} event
   * @param {ImportEventHandler} handler
   * @returns {this} For chaining.
   */
  on(event, handler) {
    let set = this._eventHandlers.get(event);
    if (!set) {
      set = new Set();
      this._eventHandlers.set(event, set);
    }
    set.add(handler);
    return this;
  }

  /**
   * Remove an event handler.
   * @param {ImportEventType} event
   * @param {ImportEventHandler} handler
   * @returns {this}
   */
  off(event, handler) {
    const handlers = this._eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
    return this;
  }

  /**
   * Emit an event to all registered handlers.
   * @param {string} event
   * @param {Object} [data]
   * @private
   */
  _emit(event, data) {
    const handlers = this._eventHandlers.get(event);
    if (!handlers) {
      return;
    }
    for (const handler of handlers) {
      try {
        handler(data);
      } catch (err) {
        console.error(`[MiiImportController] Error in '${event}' handler:`, err);
      }
    }
  }

  // // -------------------------------------------------------
  // // Active source management
  // // -------------------------------------------------------

  /** @returns {symbol | null} Currently active source ID. */
  get activeSourceId() {
    return this._activeSourceId;
  }

  /**
   * Switch the active source.
   * @param {symbol} id - Source ID returned from an add method.
   * @throws {Error} If the source ID is not registered.
   */
  setActiveSource(id) {
    if (!this._sources.has(id)) {
      throw new Error('Unknown source ID');
    }
    this._switchActive(id);
  }

  /**
   * Get the source record for an ID.
   * @param {symbol} id
   * @returns {SourceRecord | undefined}
   */
  getSource(id) {
    return this._sources.get(id);
  }

  /**
   * Internal active-source switching with event emission.
   * @param {symbol} newId
   * @private
   */
  _switchActive(newId) {
    const previousId = this._activeSourceId;
    if (previousId === newId) {
      return;
    }
    this._activeSourceId = newId;
    this._emit('sourceChanged', new ImportSourceChangedEvent(newId, previousId));
  }

  // // -------------------------------------------------------
  // // Source registration: text input
  // // -------------------------------------------------------

  /**
   * Register a text input that accepts hex, base64, or base64url Mii data.
   * Automatically parses on input with configurable debounce.
   * @param {HTMLInputElement} element - The text input element.
   * @param {Object} [opts]
   * @param {number} [opts.debounceMs] - Debounce delay in milliseconds. Default 500.
   * @returns {symbol} Source ID for this input.
   */
  addTextInput(element, opts) {
    const debounceMs = (opts && opts.debounceMs) ? opts.debounceMs : 500;
    const id = Symbol('import-source-text');
    const source = new SourceRecord(id, ImportSourceType.Text);
    source.extra = { debounceTimer: 0 };

    // Cleanup callback clears the debounce timer.
    source._disposeExtra = () => {
      clearTimeout(source.extra.debounceTimer);
    };

    const onInput = () => {
      clearTimeout(source.extra.debounceTimer);
      source.extra.debounceTimer = setTimeout(() => {
        const value = element.value.trim();
        if (value.length === 0) {
          return;
        }
        // Typing activates this source.
        this._switchActive(id);
        const data = parseHexOrBase64ExToBytes(value);
        this.submitData(id, data);
      }, debounceMs);
    };

    trackListener(source, element, 'input', onInput);
    this._sources.set(id, source);
    return id;
  }

  // // -------------------------------------------------------
  // // Source registration: file input
  // // -------------------------------------------------------

  /**
   * Register a file input that reads Mii data files.
   * Supports any binary file whose size matches a known Mii format.
   * @param {HTMLInputElement} element - File input element (type="file").
   * @returns {symbol} Source ID for this input.
   */
  addFileInput(element) {
    const id = Symbol('import-source-file');
    const source = new SourceRecord(id, ImportSourceType.File);

    const onChange = async () => {
      if (!element.files || !element.files.length) {
        return;
      }
      const file = element.files[0];
      // Selecting a file activates this source.
      this._switchActive(id);

      /*
      try {
        const buffer = await file.arrayBuffer();
        const data = new Uint8Array(buffer);
        this.submitData(id, data);
      } catch (error) {
        this._emit('error', new ImportErrorEvent(
          id,
          MiiErrorReason.FileReadError,
          error instanceof Error ? error : new Error(String(error))
        ));
      }
      */
      const reader = new FileReader();
      reader.onload = () => {
        const data = new Uint8Array(/** @type {ArrayBuffer} */ (reader.result));
        this.submitData(id, data);
      };
      reader.onerror = () => {
        const e = reader.error;
        this._emit('error', new ImportErrorEvent(
          id,
          MiiErrorReason.FileReadError,
          e instanceof Error ? e : new Error(String(e))
        ));
      };
      reader.readAsArrayBuffer(file);
    };

    // Use 'change' rather than 'input' for file inputs.
    trackListener(source, element, 'change', onChange);
    this._sources.set(id, source);
    return id;
  }

  // // -------------------------------------------------------
  // // Source registration: QR scanner
  // // -------------------------------------------------------

  /**
   * Register a QR code scanner source.
   * The controller creates the QrScanner instance from the imported module.
   * @param {Object} opts
   * @param {HTMLVideoElement} opts.videoElement - Video element for camera feed.
   * @param {HTMLElement} opts.startButton - Element that starts scanning on click.
   * @param {HTMLElement} [opts.stopButton] - Element that stops scanning on click.
   * @param {HTMLElement} opts.containerElement - Toggled visible/hidden by the controller.
   * @param {HTMLInputElement} [opts.fileInput] - Optional file input for QR image scanning.
   * @param {HTMLSelectElement} [opts.cameraSelect] - Select element the controller populates with available cameras.
   * @param {Object} [opts.scannerOptions] - Extra options forwarded to QrScanner constructor.
   * @returns {symbol} Source ID for this scanner.
   */
  addQrScanner(opts) {
    const {
      videoElement,
      startButton,
      stopButton,
      containerElement,
      fileInput,
      cameraSelect,
      scannerOptions
    } = opts;

    QrScanner.setBarcodeDetectorDisabled !== undefined && QrScanner.setBarcodeDetectorDisabled();

    const id = Symbol('import-source-qr');
    const source = new SourceRecord(id, ImportSourceType.Qr);
    source.extra = { scanner: null, scanning: false };

    /**
     * Decode handler shared by live scan and file scan.
     * @param {import('qr-scanner').default.ScanResult & {
     * binaryData: Uint8Array}} result - QrScanner scan result.
     */
    const onDecode = (result) => {
      // QrScanner result has a .binaryData array (binary QR payload).
      // , as long as you use this fork: https://github.com/getify-as-is/qr-scanner
      // mentioned in the issue: https://github.com/nimiq/qr-scanner/issues/271
      const bytes = result.binaryData;
      if (!bytes || bytes.length <= 0) {
        this._emit('error', new ImportErrorEvent(id, MiiErrorReason.QrNoBinary));
        return;
      }
      this._switchActive(id);
      this.submitData(id, new Uint8Array(bytes));
      // Stop scanning on successful decode.
      stopScanning();
    };

    // Build the QrScanner instance.
    const scanner = new QrScanner(videoElement, onDecode, {
      // @ts-ignore -- This property may have been removed.
      returnDetailedScanResult: true,
      highlightScanRegion: true,
      highlightCodeOutline: true,
      ...scannerOptions,
      onDecodeError: function (/** @type {Error|string} */ err) {
        // "No QR code found" is normal during scanning — suppress it.
        if (typeof err === 'string' && err.indexOf('No QR code found') !== -1) {
          return;
        }
        scannerOptions?.onDecodeError?.(err);
      }
    });
    source.extra.scanner = scanner;

    const startScanning = () => {
      containerElement.style.display = '';
      source.extra.scanning = true;
      this._emit('scanning');
      scanner.start().then(() => {
        // Enumerate cameras after successful start to avoid
        // creating a temporary video stream.
        QrScanner.listCameras(true).then((cameras) => {
          if (cameraSelect) {
            const existing = cameraSelect.getElementsByClassName('device-camera');
            for (const el of existing) {
              el.remove();
            }
            for (const camera of cameras) {
              const option = document.createElement('option');
              option.value = camera.id;
              option.text = camera.label;
              option.className = 'device-camera';
              cameraSelect.add(option);
            }
          }
          this._emit('camerasLoaded', new ImportCamerasLoadedEvent(cameras));
        });
      }).catch((/** @type {Error} */ error) => {
        this._emit('error', new ImportErrorEvent(
          id,
          MiiErrorReason.QrScanError,
          error instanceof Error ? error : new Error(String(error))
        ));
      });
    };

    const stopScanning = () => {
      scanner.stop();
      containerElement.style.display = 'none';
      source.extra.scanning = false;
      this._emit('scannerStopped');
    };

    if (cameraSelect) {
      trackListener(source, cameraSelect, 'change', () => {
        scanner.setCamera(cameraSelect.value);
      });
    }

    trackListener(source, startButton, 'click', startScanning);
    if (stopButton) {
      trackListener(source, stopButton, 'click', stopScanning);
    }

    // Optional: scan QR from an image file.
    if (fileInput) {
      const onFileChange = () => {
        if (!fileInput.files || !fileInput.files.length) {
          return;
        }
        const file = fileInput.files[0];
        this._switchActive(id);
        QrScanner.scanImage(file,
          // @ts-ignore -- This property may have been removed.
          { returnDetailedScanResult: true })
          .then(onDecode)
          .catch(() => {
            this._emit('error', new ImportErrorEvent(id, MiiErrorReason.QrNotFound));
          });
      };
      trackListener(source, fileInput, 'change', onFileChange);
    }

    // Hide the container initially.
    containerElement.style.display = 'none';

    /** Type-specific cleanup to stop and destroy the scanner. */
    source._disposeExtra = () => {
      try {
        scanner.stop();
        scanner.destroy();
      } catch (e) {
        // Scanner may already be destroyed.
      }
    };

    this._sources.set(id, source);
    return id;
  }

  // // -------------------------------------------------------
  // // Source registration: custom source
  // // -------------------------------------------------------

  /**
   * Register a custom source that participates in active-source switching.
   * The consumer is responsible for obtaining data and calling
   * {@link submitData} when ready.
   * @returns {symbol} Source ID.
   */
  addCustomSource() {
    const id = Symbol('import-source-custom');
    const source = new SourceRecord(id, ImportSourceType.Custom);
    this._sources.set(id, source);
    return id;
  }

  // // -------------------------------------------------------
  // // Universal data submission
  // // -------------------------------------------------------

  /**
   * Submit raw Mii data from any source for decoding.
   * Built-in sources call this internally; custom sources call it directly.
   * On success, fires a 'parsed' event. On failure, fires an 'error' event.
   * @param {symbol} sourceId - Source ID that produced this data.
   * @param {Uint8Array} data - Raw Mii data bytes.
   * @throws {Error} Throws if the source symbol ID does not exist.
   */
  submitData(sourceId, data) {
    if (this._disposed) {
      return;
    }

    const source = this._sources.get(sourceId);
    if (!source) {
      throw new Error('Unknown source ID');
    }

    // Decrypt WrappedStoreData (Mii QR code) if the data is large enough.
    data = decryptIfWrappedStoreData(data);

    // Attempt decoding through MiiDecoder.
    const studioData = new Uint8Array(MiiDecoder.SIZE_STUDIO_RAW_DATA);
    const extraInfo = new MiiExtraInfo();

    const decoded = MiiDecoder.fromAnyMiiDataExtra(
      studioData, extraInfo, data.length, data);
    if (!decoded) {
      source.lastRawData = null;
      source.lastStudioData = null;
      source.lastExtraInfo = null;
      this._emit('error', new ImportErrorEvent(
        sourceId,
        MiiErrorReason.UnsupportedSize
      ));
      return;
    }

    // Validate the converted Studio data.
    if (!MiiDecoder.isValid(studioData)) {
      source.lastRawData = data;
      source.lastStudioData = null;
      source.lastExtraInfo = null;
      this._emit('error', new ImportErrorEvent(
        sourceId,
        MiiErrorReason.ValidationFailed
      ));
      return;
    }

    // Build the obfuscated URL hex.
    const studioHexUrl = buildStudioHexUrl(studioData);

    // Store on the source record.
    source.lastRawData = data;
    source.lastStudioData = studioData;
    source.lastExtraInfo = extraInfo;

    this._emit('parsed', new ImportParsedEvent(
      sourceId,
      data,
      studioData,
      studioHexUrl,
      extraInfo
    ));
  }

  // // -------------------------------------------------------
  // // Cleanup
  // // -------------------------------------------------------

  /**
   * Dispose of the controller.
   * Delegates cleanup to each source record's own dispose method.
   * The controller is unusable after this call.
   */
  dispose() {
    this._disposed = true;

    for (const source of this._sources.values()) {
      source.dispose();
    }

    this._sources.clear();
    this._eventHandlers.clear();
    this._activeSourceId = null;
  }
}
